import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { fetchDownloadSettings, SUPPORT_EMAIL } from "@/lib/api";

const PLANS = [
  { id: "basic", name: "Basic", count: 3, blurb: "1 Complimentary + Any 3 Additional Challenges", perks: ["Detailed Breakdown of the Complimentary Challenge", "Detailed Breakdown of the 3 Additional Challenges"] },
  { id: "pro", name: "Pro", count: 7, blurb: "1 Complimentary + Any 7 Additional Challenges", popular: true, perks: ["Detailed Breakdown of the Complimentary Challenge", "Detailed Breakdown of the 7 Additional Challenges"] },
];

const FAQS = [
  ["Is this a recurring subscription?", "No — Basic and Pro are one-time credit packs, not subscriptions."],
  ["Do credits expire?", "No. Your challenge credits stay in your account until you use them."],
  ["Can I get a refund?", "Digital credits are non-refundable once a purchased challenge is started, except for verified technical failure — see our Terms of Usage. The app shows you the correct refund route for your purchase."],
  ["Are payments secure?", "Yes. We use secured payment partners and we never store your payment information — your card, UPI and account details stay with the payment partner."],
  ["How much do the plans cost?", "Pricing and payment options can vary by platform, so the exact price is always shown in the app before you pay. Download the app to see current plans and offers."],
  ["Where do my payments go?", "13% of TruCitizen's net profits go to the National Defence Fund."],
];

const StoreBadges = ({ settings, id }) => (
  <div id={id} className="flex flex-wrap gap-3" data-testid="premium-store-badges">
    {[["android", "fa-brands fa-google-play", "GET IT ON", "Google Play"], ["ios", "fa-brands fa-apple", "Download on the", "App Store"]].map(([k, icon, small, big]) => (
      <a key={k} href={settings?.[k]?.url || "#"} target="_blank" rel="noopener noreferrer" data-testid={`premium-badge-${k}`}
        className="inline-flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#1C1C1E] text-white hover:bg-black transition-colors min-w-[168px]">
        <i className={`${icon} text-2xl`} aria-hidden="true" />
        <span className="leading-tight text-left"><span className="block text-[10px] uppercase tracking-wide text-white/70">{small}</span><span className="block text-base font-semibold">{big}</span></span>
      </a>
    ))}
  </div>
);

const Faq = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-black/[0.08]">
      <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`premium-faq-${i}`} className="w-full py-5 flex items-center justify-between gap-4 text-left font-semibold text-[#1C1C1E]">
        {q}<ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180 text-[#FF6600]" : "text-[#8E8E93]"}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pb-5 text-[#8E8E93] leading-relaxed">{a}</motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Premium() {
  const { data: settings } = useQuery({ queryKey: ["download-settings"], queryFn: fetchDownloadSettings, staleTime: 60_000 });
  const origin = window.location.origin;
  const goApp = (e) => {
    e.preventDefault();
    const el = document.getElementById("get-the-app");
    const scroll = () => (window.__lenis ? window.__lenis.scrollTo(el, { offset: -110 }) : el?.scrollIntoView({ behavior: "smooth" }));
    if (/Android|iPhone|iPad/i.test(navigator.userAgent)) {
      window.location.href = "trucitizen://premium";
      setTimeout(scroll, 900);
    } else scroll();
  };

  return (
    <div className="premium -mt-2 bg-[#FFF5ED] text-[#1C1C1E]" style={{ fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif" }} data-testid="premium-page">
      <SEO title="Become Pro – TruCitizen" description="Unlock 125+ TruCitizen challenges across India, States & UTs and Communities with one-time Basic (3 additional challenges) and Pro (7 additional challenges) credit packs — no subscriptions. 13% of net profits go to the National Defence Fund." path="/premium"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Product", name: "TruCitizen Premium challenge credits", brand: { "@type": "Brand", name: "TruCitizen" }, url: `${origin}/premium`,
          description: "One-time challenge credit packs (Basic: 3 additional challenges, Pro: 7 additional challenges) that unlock additional TruCitizen challenges.",
        }} />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 sm:pt-36 pb-10 sm:pb-16">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6600]">Become Pro</p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl" data-testid="premium-title">Prove your knowledge of India. Unlock every challenge.</h1>
          <div className="mt-5 h-1 w-40 rounded-full bg-[linear-gradient(90deg,#FF9933_0%,#FF9933_33%,#fff_33%,#fff_66%,#138808_66%)]" aria-hidden="true" />
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#8E8E93] leading-relaxed">Only the Indian Citizenship Challenge is complimentary. Premium unlocks the remaining 125+ challenges across India, States &amp; UTs and Communities.</p>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <StoreBadges settings={settings} id="get-the-app" />
            <a href="#pricing" onClick={(e) => { e.preventDefault(); const el = document.getElementById("pricing"); window.__lenis ? window.__lenis.scrollTo(el, { offset: -110 }) : el.scrollIntoView({ behavior: "smooth" }); }} data-testid="premium-compare-link"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl border-2 border-[#FF6600] text-[#FF6600] font-semibold hover:bg-[#FF6600] hover:text-white transition-colors">Compare plans</a>
          </div>
        </Reveal>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="premium-pricing">
        <div className="grid md:grid-cols-2 gap-6">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <div className={`relative h-full rounded-[20px] p-7 sm:p-8 bg-white shadow-[0_20px_50px_-30px_rgba(28,28,30,0.35)] ${p.popular ? "ring-2 ring-[#D4A017] shadow-[0_0_0_8px_#FFF3D6,0_24px_60px_-30px_rgba(212,160,23,0.45)]" : "border border-black/5"}`} data-testid={`plan-card-${p.id}`}>
                {p.popular && <span className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-[#D4A017] text-white text-xs font-bold tracking-wide shadow" data-testid="plan-most-popular">Most popular</span>}
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8E8E93]">{p.name}</p>
                <div className="mt-3 flex items-end gap-2">
                  <span className="text-5xl font-bold tracking-tight" data-testid={`plan-count-${p.id}`}>{p.count}</span>
                  <span className="mb-2 text-sm text-[#8E8E93]">additional challenges</span>
                </div>
                <p className="mt-1 font-bold text-[#1C1C1E]" data-testid={`plan-blurb-${p.id}`}>{p.blurb}</p>
                <ul className="mt-5 space-y-2.5" data-testid={`plan-perks-${p.id}`}>
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2.5 text-sm text-[#1C1C1E]">
                      <Check size={16} className="shrink-0 mt-0.5 text-[#138808]" /> {perk}
                    </li>
                  ))}
                </ul>
                <button onClick={goApp} data-testid={`plan-cta-${p.id}`} className="mt-7 w-full py-3.5 rounded-xl bg-[#FF6600] text-white font-semibold hover:bg-[#e65c00] active:scale-[0.99] transition-all">Continue in the app</button>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-[#8E8E93] leading-relaxed max-w-3xl" data-testid="pricing-note">Basic and Pro are one-time purchases of challenge credits, not recurring subscriptions. Pricing and payment options vary by platform and are shown in the app before you pay — download the app for full details. We use secured payment partners and never store your payment information.</p>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-10" data-testid="premium-faq">
        <Reveal><h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Frequently asked</h2></Reveal>
        <div className="mt-6">{FAQS.map(([q, a], i) => <Faq key={q} q={q} a={a} i={i} />)}</div>
      </section>

      {/* Legal strip */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-20 text-sm text-[#8E8E93]" data-testid="premium-legal">
        <div className="rounded-2xl bg-white border border-black/5 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2 font-semibold text-[#1C1C1E]">
            <a href="https://trucitizen.in/privacy-policy" className="hover:text-[#FF6600]">Privacy Policy</a>
            <a href="https://trucitizen.in/terms" className="hover:text-[#FF6600]">Terms Of Usage</a>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("[TruCitizen] Premium")}`} className="hover:text-[#FF6600]">Support: {SUPPORT_EMAIL}</a>
          </div>
          <p>CreJik Technologies Private Limited, A6-A9, New Haven, Palghar, Maharashtra - 401501</p>
        </div>
      </section>
    </div>
  );
}
